import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import api, { fmtIDR } from "../../lib/api";
import { inputCls, Field, Panel, btnPrimary, errMsg } from "./ui";

function PriceRow({ p, gameName, bulk, onSaved }) {
  const [v, setV] = useState(p.sell_price_idr);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(p.sell_price_idr); }, [p.sell_price_idr]);
  const custom = bulk !== undefined && p.sell_price_idr !== bulk;
  const save = async () => {
    if (!(Number(v) > 0)) { toast.error("Harga harus lebih dari 0"); return; }
    setBusy(true);
    try {
      await api.patch(`/products/${p.id}`, { sell_price_idr: Number(v) });
      toast.success(`Harga ${p.name} disimpan`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`bprice-row-${p.id}`} className="border-b border-[#35353B]">
      <td className="py-2 pr-3 text-[#A8A8AD]">{gameName}</td>
      <td className="py-2 pr-3 text-[#F5F3ED]">{p.name}{custom && <span data-testid={`bprice-custom-${p.id}`} className="ml-2 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37] rounded-full px-2 py-0.5">Khusus</span>}</td>
      <td className="py-2 pr-3"><input data-testid={`bprice-input-${p.id}`} type="number" min="1" className={`${inputCls} !py-1.5 w-36`} value={v} onChange={(e) => setV(e.target.value)} /></td>
      <td className="py-2 text-xs text-[#A8A8AD] pr-3">/ {p.unit || "unit"}</td>
      <td className="py-2"><button data-testid={`bprice-save-${p.id}`} onClick={save} disabled={busy || Number(v) === p.sell_price_idr} className="text-xs font-bold text-[#D4AF37] hover:text-[#F2D98D] disabled:opacity-30 transition-colors">{busy ? "..." : "Simpan"}</button></td>
    </tr>
  );
}

export default function ProductPricePanel({ products, games, cats, onSaved }) {
  const [bulks, setBulks] = useState({});
  const [gameId, setGameId] = useState("");
  const [cat, setCat] = useState("coins");
  const [price, setPrice] = useState("");
  const [override, setOverride] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const loadBulks = () => api.get("/settings/bulk-price").then((r) => setBulks(r.data));
  useEffect(() => { loadBulks(); }, []);
  useEffect(() => {
    if (!gameId && games.length) setGameId((games.find((g) => /aion\s*2/i.test(g.name)) || games[0]).id);
  }, [games, gameId]);
  const key = `${gameId}:${cat}`;
  useEffect(() => { setPrice(bulks[key] ?? ""); }, [key, bulks]);
  const gameName = (id) => games.find((g) => g.id === id)?.name || "-";
  const catName = cats.find((c) => c.slug === cat)?.name || cat;
  const scope = products.filter((p) => !p.deleted && p.game_id === gameId && p.category_slug === cat);
  const apply = async () => {
    const v = Number(price);
    if (!(v > 0)) { toast.error("Isi harga jual lebih dari 0"); return; }
    const note = override ? "SEMUA harga (termasuk Khusus) akan ditimpa." : "Produk bertanda Khusus tidak diubah.";
    if (!window.confirm(`Set harga ${fmtIDR(v)} untuk ${catName} ${gameName(gameId)} (${scope.length} produk)? ${note}`)) return;
    setBusy(true);
    try {
      const r = await api.put("/products-bulk-price", { game_id: gameId, category_slug: cat, sell_price_idr: v, override_custom: override });
      toast.success(`Harga diterapkan ke ${r.data.updated} produk${r.data.custom_kept ? `, ${r.data.custom_kept} produk khusus dipertahankan` : ""}`);
      setOverride(false); loadBulks(); onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const term = q.trim().toLowerCase();
  const rows = term ? products.filter((p) => !p.deleted && (p.name.toLowerCase().includes(term) || gameName(p.game_id).toLowerCase().includes(term))) : [];
  return (
    <Panel title="Harga Jual Massal" testId="bulk-price-panel">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <Field label="Game">
          <select data-testid="bprice-game" className={inputCls} value={gameId} onChange={(e) => setGameId(e.target.value)}>
            {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Kategori">
          <select data-testid="bprice-category" className={inputCls} value={cat} onChange={(e) => setCat(e.target.value)}>
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Harga jual (IDR per unit)">
          <input data-testid="bprice-input" type="number" min="1" placeholder="13000" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <button data-testid="bprice-apply-btn" onClick={apply} disabled={busy || !gameId || price === ""} className={btnPrimary}>{busy ? "Menerapkan..." : `Terapkan ke ${scope.length} Produk`}</button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 mb-6 pb-6 border-b border-[#35353B]">
        <label className="flex items-center gap-2 text-sm text-[#F5F3ED] cursor-pointer">
          <input data-testid="bprice-override-checkbox" type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="accent-[#D4AF37]" />
          Timpa juga produk dengan harga khusus
        </label>
        <p data-testid="bprice-current" className="text-xs text-[#A8A8AD]">Harga massal saat ini: {bulks[key] ? fmtIDR(bulks[key]) : "belum diatur"}. Tanpa centang, produk bertanda <span className="text-[#D4AF37]">Khusus</span> tidak berubah.</p>
      </div>
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8AD]" />
        <input data-testid="bprice-search-input" placeholder="Cari game atau server untuk atur harga manual, mis. Siel" className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {term && (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <tbody>{rows.slice(0, 60).map((p) => <PriceRow key={p.id} p={p} gameName={gameName(p.game_id)} bulk={bulks[`${p.game_id}:${p.category_slug}`]} onSaved={onSaved} />)}</tbody>
          </table>
          {rows.length === 0 && <p data-testid="bprice-no-results" className="text-xs text-[#A8A8AD] py-3">Tidak ada produk yang cocok.</p>}
          {rows.length > 60 && <p className="text-xs text-[#A8A8AD] py-3">Menampilkan 60 dari {rows.length} produk, persempit pencarian.</p>}
        </div>
      )}
    </Panel>
  );
}
